import { CanAccess } from "ra-core";
import { TaskDeleteAction } from "./TaskDeleteAction";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { taskPeriodLabel } from "./taskPeriod";
import { isInstallation, installationRangeLabel } from "./installation";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { TaskEdit } from "./TaskEdit";
import { TaskEditSheet } from "./TaskEditSheet";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  type Identifier,
  useGetIdentity,
  useGetOne,
  useNotify,
  useUpdate,
} from "ra-core";

import { ReferenceField } from "@/components/admin/reference-field";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Separator } from "@/components/ui/separator";

import type { Task } from "../types";
import { useConfigurationContext } from "../root/ConfigurationContext";

import { TaskComments } from "./TaskComments";
import { TaskExpenses } from "./TaskExpenses";
import { TaskSchedule } from "./TaskSchedule";
import { TaskContacts } from "./TaskContacts";

const taskStatuses = [
  {
    value: "new",
    label: "Новая",
  },
  {
    value: "in_progress",
    label: "В работе",
  },
  {
    value: "completed",
    label: "Выполнена",
  },
];

export const TaskWorkDialog = ({
  taskId,
  open,
  onOpenChange,
}: {
  taskId: Identifier;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const notify = useNotify();
  const [editing, setEditing] = useState(false);
  const [section, setSection] = useState("information");
  useEffect(() => {
    setSection("information");
  }, [taskId, open]);
  const mobile = useIsMobile();
  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  const { identity } = useGetIdentity();

  const { taskTypes } = useConfigurationContext();

  const {
    data: task,
    isPending,
    refetch,
    error,
  } = useGetOne<Task>(
    "tasks",
    {
      id: taskId,
    },
    {
      enabled: open && taskId != null,
    },
  );

  const [update, { isPending: isUpdating }] = useUpdate();

  const canEditTask =
    !!identity &&
    !!task &&
    (identity.administrator === true ||
      task.created_by_sales_id === identity.id);

  if (editing)
    return mobile ? (
      <TaskEditSheet
        open
        onOpenChange={(next) => {
          if (!next) setEditing(false);
        }}
        taskId={taskId}
      />
    ) : (
      <TaskEdit
        open
        taskId={taskId}
        close={() => setEditing(false)}
        onDeleted={() => onOpenChange(false)}
      />
    );

  const installation = task && isInstallation(task);
  const taskTypeLabel =
    taskTypes.find((type) => type.value === task?.type)?.label ??
    task?.type ??
    "";

  const handleStatusChange = (status: string) => {
    if (!task) {
      return;
    }

    const completed = status === "completed";

    update(
      "tasks",
      {
        id: task.id,

        data: {
          status,

          done_date: completed
            ? (task.done_date ?? new Date().toISOString())
            : null,
        },

        previousData: task,
      },
      {
        onSuccess: async () => {
          await refetch();

          notify("Статус задачи изменён", {
            type: "success",
          });
        },

        onError: () => {
          notify("Не удалось изменить статус задачи", {
            type: "error",
          });
        },
      },
    );
  };

  const PanelHeader = mobile ? SheetHeader : DialogHeader;
  const PanelTitle = mobile ? SheetTitle : DialogTitle;
  const header = (
    <PanelHeader
      className={
        mobile ? "shrink-0 border-b p-4 pr-14 text-left" : "shrink-0 pr-8"
      }
    >
      <PanelTitle className="break-words text-left leading-snug">
        {task?.text ?? "Загрузка задачи…"}
      </PanelTitle>
    </PanelHeader>
  );
  const content = (
    <>
      {error ? (
        <p role="alert" className="py-8 text-sm">
          Задача удалена или больше вам недоступна.
        </p>
      ) : isPending || !task ? (
        <div className="py-8 text-sm text-muted-foreground">Загрузка...</div>
      ) : (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
          <SegmentedControl
            label="Разделы задачи"
            value={section}
            onValueChange={setSection}
            className="w-full shrink-0"
            options={
              installation
                ? [{ value: "information", label: "Информация" }]
                : [
                    { value: "information", label: "Информация" },
                    { value: "comments", label: "Комментарии" },
                    { value: "expenses", label: "Расходы" },
                  ]
            }
          />
          <section
            data-mobile-tab-panel
            hidden={section !== "information"}
            aria-label="Информация по задаче"
            className={`min-h-0 min-w-0 flex-1 overflow-y-auto space-y-5 px-1 pb-1 [scrollbar-gutter:stable] ${installation ? "" : "border-t pt-4"}`}
          >
            {canEditTask && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="self-start min-h-11"
                  onClick={() => setEditing(true)}
                >
                  Редактировать задачу
                </Button>
                <TaskDeleteAction
                  task={task}
                  onDeleted={() => onOpenChange(false)}
                />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Тип задачи
                </div>

                <div>
                  {installation
                    ? "Монтаж · событие для всей команды"
                    : taskTypeLabel}
                </div>
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Ответственный
                </div>

                {task.team_task ? (
                  <span>Вся команда</span>
                ) : task.sales_id != null ? (
                  <ReferenceField
                    source="sales_id"
                    reference="sales"
                    record={task}
                    link={false}
                    render={({ referenceRecord }) => (
                      <span>
                        {[
                          referenceRecord?.first_name,
                          referenceRecord?.last_name,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </span>
                    )}
                  />
                ) : (
                  <span>—</span>
                )}
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">Период</div>

                {installation ? (
                  <span>
                    {installationRangeLabel(task)}
                    <span className="block text-xs text-muted-foreground">
                      Последний день включительно
                    </span>
                  </span>
                ) : (
                  <span>{taskPeriodLabel(task, true)}</span>
                )}
              </div>

              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Постановщик
                </div>

                {task.created_by_sales_id != null ? (
                  <ReferenceField
                    source="created_by_sales_id"
                    reference="sales"
                    record={task}
                    link={false}
                    render={({ referenceRecord }) => (
                      <span>
                        {[
                          referenceRecord?.first_name,
                          referenceRecord?.last_name,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </span>
                    )}
                  />
                ) : (
                  <span>—</span>
                )}
              </div>

              {task.deal_id != null && (
                <div className="sm:col-span-2">
                  <div className="text-xs text-muted-foreground mb-1">
                    Заказ / проект
                  </div>

                  <ReferenceField
                    source="deal_id"
                    reference="project_lookup"
                    record={task}
                    link={false}
                    render={({ referenceRecord }) => (
                      <span>
                        {referenceRecord?.name ?? "Проект недоступен"}
                      </span>
                    )}
                  />
                </div>
              )}
            </div>

            {!installation && (
              <CanAccess resource="contacts" action="list">
                <TaskContacts
                  task={task}
                  canEdit={canEditTask}
                  onChanged={async () => {
                    await refetch();
                  }}
                />
              </CanAccess>
            )}

            <Separator />

            <div>
              <div className="text-sm font-medium mb-2">Статус</div>

              <Select
                value={task.status ?? "new"}
                onValueChange={handleStatusChange}
                disabled={
                  isUpdating ||
                  (!canEditTask &&
                    String(task.sales_id) !== String(identity?.id))
                }
              >
                <SelectTrigger className="w-full sm:w-[220px]">
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  {taskStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!installation && (
              <TaskSchedule
                task={task}
                onSaved={async () => {
                  await refetch();
                }}
              />
            )}
          </section>
          {!installation && (
            <>
              <section
                data-mobile-tab-panel
                hidden={section !== "comments"}
                aria-label="Обсуждение задачи"
                className="min-h-0 min-w-0 flex-1 overflow-y-auto p-1 [scrollbar-gutter:stable]"
              >
                <TaskComments taskId={task.id} />
              </section>
              <section
                data-mobile-tab-panel
                hidden={section !== "expenses"}
                aria-label="Расходы по задаче"
                className="min-h-0 min-w-0 flex-1 overflow-y-auto p-1 [scrollbar-gutter:stable]"
              >
                <TaskExpenses taskId={task.id} />
              </section>
            </>
          )}
        </div>
      )}
    </>
  );
  return mobile ? (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="h-dvh flex flex-col [&>button]:z-20"
        aria-describedby={undefined}
      >
        {header}
        <div className="min-h-0 flex flex-col flex-1 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {content}
        </div>
      </SheetContent>
    </Sheet>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="lg:max-w-2xl h-[min(780px,90dvh)] max-h-[90dvh] flex flex-col overflow-hidden"
        aria-describedby={undefined}
      >
        {header}
        {content}
      </DialogContent>
    </Dialog>
  );
};
